/**
 * TaskForm Component
 *
 * Form for creating and editing tasks.
 * Supports all task fields with validation.
 *
 * @module components/tasks/TaskForm
 */

import { memo, useState, useCallback, useEffect } from 'react';
import {
  X,
  Save,
  Loader2,
  AlertCircle
} from 'lucide-react';
import TaskPriorityBadge, { TASK_PRIORITY_CONFIG } from './TaskPriorityBadge.jsx';
import TaskStatusBadge, { TASK_STATUS_CONFIG } from './TaskStatusBadge.jsx';
import TaskLabelBadge from './TaskLabelBadge.jsx';

// ============================================
// Constants
// ============================================

/**
 * Default form values for new tasks
 */
const DEFAULT_VALUES = {
  title: '',
  description: '',
  status: 'backlog',
  priority: 'medium',
  estimatedMinutes: '',
  labelIds: [],
  assigneeId: '',
  assigneeType: 'user'
};

/**
 * Priority options for select
 */
const PRIORITY_OPTIONS = Object.entries(TASK_PRIORITY_CONFIG).map(([value, config]) => ({
  value,
  label: config.label
}));

/**
 * Status options for select (only for edit mode)
 */
const STATUS_OPTIONS = Object.entries(TASK_STATUS_CONFIG).map(([value, config]) => ({
  value,
  label: config.label
}));

// ============================================
// Helper Components
// ============================================

/**
 * Form field wrapper with label and error
 */
const FormField = memo(function FormField({
  label,
  required = false,
  error,
  children
}) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-300 mb-1.5">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      {children}
      {error && (
        <div className="flex items-center gap-1 mt-1 text-xs text-red-400">
          <AlertCircle className="w-3 h-3" />
          {error}
        </div>
      )}
    </div>
  );
});

/**
 * Text input component
 */
const TextInput = memo(function TextInput({
  value,
  onChange,
  placeholder,
  error,
  ...props
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`
        w-full px-3 py-2 bg-gray-800 border rounded-lg
        text-gray-200 placeholder-gray-500
        focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
        transition-colors
        ${error ? 'border-red-500' : 'border-gray-700'}
      `}
      {...props}
    />
  );
});

/**
 * Textarea component
 */
const TextArea = memo(function TextArea({
  value,
  onChange,
  placeholder,
  rows = 4,
  error,
  ...props
}) {
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className={`
        w-full px-3 py-2 bg-gray-800 border rounded-lg
        text-gray-200 placeholder-gray-500 resize-none
        focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
        transition-colors
        ${error ? 'border-red-500' : 'border-gray-700'}
      `}
      {...props}
    />
  );
});

/**
 * Select component
 */
const Select = memo(function Select({
  value,
  onChange,
  options,
  placeholder,
  error,
  ...props
}) {
  return (
    <select
      value={value}
      onChange={onChange}
      className={`
        w-full px-3 py-2 bg-gray-800 border rounded-lg
        text-gray-200
        focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
        transition-colors
        ${error ? 'border-red-500' : 'border-gray-700'}
      `}
      {...props}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
});

/**
 * Priority selector with visual badges
 */
const PrioritySelector = memo(function PrioritySelector({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {PRIORITY_OPTIONS.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`
            transition-all duration-200
            ${value === opt.value
              ? 'ring-2 ring-indigo-500 ring-offset-2 ring-offset-gray-900'
              : 'opacity-60 hover:opacity-100'
            }
          `}
        >
          <TaskPriorityBadge priority={opt.value} size="md" />
        </button>
      ))}
    </div>
  );
});

/**
 * Label selector component
 */
const LabelSelector = memo(function LabelSelector({
  availableLabels = [],
  selectedIds = [],
  onChange
}) {
  const toggleLabel = useCallback((labelId) => {
    if (selectedIds.includes(labelId)) {
      onChange(selectedIds.filter(id => id !== labelId));
    } else {
      onChange([...selectedIds, labelId]);
    }
  }, [selectedIds, onChange]);

  if (availableLabels.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No labels available
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {availableLabels.map(label => (
        <button
          key={label.id}
          type="button"
          onClick={() => toggleLabel(label.id)}
          className={`
            transition-all duration-200
            ${selectedIds.includes(label.id)
              ? 'ring-2 ring-indigo-500 ring-offset-2 ring-offset-gray-900'
              : 'opacity-60 hover:opacity-100'
            }
          `}
        >
          <TaskLabelBadge label={label} size="md" />
        </button>
      ))}
    </div>
  );
});

// ============================================
// Main Component
// ============================================

/**
 * TaskForm Component
 *
 * @param {object} props - Component props
 * @param {object} [props.task] - Task to edit (null for create mode)
 * @param {array} [props.projects=[]] - Available projects
 * @param {array} [props.features=[]] - Available features for selected project
 * @param {array} [props.labels=[]] - Available labels
 * @param {array} [props.assignees=[]] - Available assignees (users/agents)
 * @param {function} props.onSubmit - Submit handler (receives form data)
 * @param {function} [props.onCancel] - Cancel handler
 * @param {boolean} [props.isSubmitting=false] - Whether form is submitting
 * @param {string} [props.submitError] - Submit error message
 * @param {string} [props.className=''] - Additional CSS classes
 * @returns {React.ReactElement} Form element
 */
const TaskForm = memo(function TaskForm({
  task,
  projects = [],
  features = [],
  labels = [],
  assignees = [],
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitError,
  className = ''
}) {
  const isEditMode = !!task;

  // Form state
  const [formData, setFormData] = useState(() => {
    if (task) {
      return {
        title: task.title || '',
        description: task.description || '',
        status: task.status || 'backlog',
        priority: task.priority || 'medium',
        estimatedMinutes: task.estimatedMinutes?.toString() || '',
        projectId: task.projectId || '',
        featureId: task.featureId || '',
        labelIds: task.labels?.map(l => l.id) || [],
        assigneeId: task.assigneeId || '',
        assigneeType: task.assigneeType || 'user'
      };
    }
    // Create mode: auto-select first project if available
    return {
      ...DEFAULT_VALUES,
      projectId: projects[0]?.id || ''
    };
  });

  const [errors, setErrors] = useState({});

  // Reset form when task changes
  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        status: task.status || 'backlog',
        priority: task.priority || 'medium',
        estimatedMinutes: task.estimatedMinutes?.toString() || '',
        projectId: task.projectId || '',
        featureId: task.featureId || '',
        labelIds: task.labels?.map(l => l.id) || [],
        assigneeId: task.assigneeId || '',
        assigneeType: task.assigneeType || 'user'
      });
      setErrors({});
    }
  }, [task]);

  // Update field handler
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error on change
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  // Validate form
  const validate = useCallback(() => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 200) {
      newErrors.title = 'Title must be 200 characters or less';
    }

    if (formData.description && formData.description.length > 5000) {
      newErrors.description = 'Description must be 5000 characters or less';
    }

    if (formData.estimatedMinutes) {
      const minutes = parseInt(formData.estimatedMinutes, 10);
      if (isNaN(minutes) || minutes < 0) {
        newErrors.estimatedMinutes = 'Must be a positive number';
      }
    }

    if (!formData.projectId) {
      newErrors.projectId = 'Project is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Submit handler
  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    if (!validate()) return;

    const submitData = {
      title: formData.title.trim(),
      description: formData.description.trim(), // Must be string, not null
      priority: formData.priority,
      projectId: formData.projectId,
      featureId: formData.featureId || null,
      labelIds: formData.labelIds // Always send array (can be empty)
    };

    // Only include estimatedMinutes if set (backend expects number, not null)
    if (formData.estimatedMinutes) {
      submitData.estimatedMinutes = parseInt(formData.estimatedMinutes, 10);
    }

    // Include status only in edit mode
    if (isEditMode) {
      submitData.status = formData.status;
    }

    // Include assignee if set
    if (formData.assigneeId) {
      submitData.assigneeId = formData.assigneeId;
      submitData.assigneeType = formData.assigneeType;
    }

    console.log('[TaskForm] Submitting:', submitData);
    onSubmit(submitData);
  }, [formData, validate, onSubmit, isEditMode]);

  // Project options
  const projectOptions = projects.map(p => ({
    value: p.id,
    label: p.displayName || p.name
  }));

  // Feature options (filtered by selected project)
  const featureOptions = features
    .filter(f => !formData.projectId || f.projectId === formData.projectId)
    .map(f => ({
      value: f.id,
      label: f.name
    }));

  // Assignee options
  const assigneeOptions = assignees.map(a => ({
    value: a.id,
    label: `${a.name} (${a.type})`
  }));

  return (
    <form
      onSubmit={handleSubmit}
      className={`bg-gray-900 rounded-lg ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700">
        <h2 className="text-lg font-semibold text-gray-100">
          {isEditMode ? 'Edit Task' : 'Create Task'}
        </h2>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Form Fields */}
      <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
        {/* Title */}
        <FormField label="Title" required error={errors.title}>
          <TextInput
            value={formData.title}
            onChange={(e) => updateField('title', e.target.value)}
            placeholder="Enter task title..."
            error={errors.title}
            maxLength={200}
          />
        </FormField>

        {/* Description */}
        <FormField label="Description" error={errors.description}>
          <TextArea
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder="Enter task description..."
            rows={4}
            error={errors.description}
          />
        </FormField>

        {/* Project */}
        <FormField label="Project" required error={errors.projectId}>
          <Select
            value={formData.projectId}
            onChange={(e) => {
              updateField('projectId', e.target.value);
              updateField('featureId', ''); // Reset feature on project change
            }}
            options={projectOptions}
            placeholder="Select project..."
            error={errors.projectId}
          />
        </FormField>

        {/* Feature (optional) */}
        {featureOptions.length > 0 && (
          <FormField label="Feature">
            <Select
              value={formData.featureId}
              onChange={(e) => updateField('featureId', e.target.value)}
              options={[{ value: '', label: 'No feature' }, ...featureOptions]}
            />
          </FormField>
        )}

        {/* Priority */}
        <FormField label="Priority">
          <PrioritySelector
            value={formData.priority}
            onChange={(value) => updateField('priority', value)}
          />
        </FormField>

        {/* Status (edit mode only) */}
        {isEditMode && (
          <FormField label="Status">
            <Select
              value={formData.status}
              onChange={(e) => updateField('status', e.target.value)}
              options={STATUS_OPTIONS}
            />
          </FormField>
        )}

        {/* Estimated Time */}
        <FormField label="Estimated Time (minutes)" error={errors.estimatedMinutes}>
          <TextInput
            type="number"
            value={formData.estimatedMinutes}
            onChange={(e) => updateField('estimatedMinutes', e.target.value)}
            placeholder="e.g. 60"
            min="0"
            error={errors.estimatedMinutes}
          />
        </FormField>

        {/* Labels */}
        {labels.length > 0 && (
          <FormField label="Labels">
            <LabelSelector
              availableLabels={labels}
              selectedIds={formData.labelIds}
              onChange={(ids) => updateField('labelIds', ids)}
            />
          </FormField>
        )}

        {/* Assignee */}
        {assignees.length > 0 && (
          <FormField label="Assignee">
            <Select
              value={formData.assigneeId}
              onChange={(e) => {
                const selectedId = e.target.value;
                const selectedAssignee = assignees.find(a => a.id === selectedId);
                updateField('assigneeId', selectedId);
                // Update assigneeType based on selected assignee
                if (selectedAssignee) {
                  updateField('assigneeType', selectedAssignee.type);
                }
              }}
              options={[{ value: '', label: 'Unassigned' }, ...assigneeOptions]}
            />
          </FormField>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-700">
        {submitError && (
          <div className="flex items-center gap-2 mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {submitError}
          </div>
        )}

        <div className="flex justify-end gap-3">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting}
            className="
              flex items-center gap-2 px-4 py-2
              bg-indigo-600 hover:bg-indigo-700 text-white
              rounded-lg transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {isEditMode ? 'Saving...' : 'Creating...'}
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditMode ? 'Save Changes' : 'Create Task'}
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
});

export default TaskForm;
